/**
 * Company Profile Form Component
 * 
 * Step 3 of the wizard - Complete company legal profile with:
 * - Legal form
 * - Activity type
 * - Tax regime
 * - Business metrics
 * - Special status flags
 */

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft, ArrowRight, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import type { RCExtractedData, CompanyLegalProfile, LegalForm, ActivityType, TaxRegime } from '@lebusiness/types';

const profileSchema = z.object({
  legal_form: z.enum(['EURL', 'SARL', 'SPA', 'SNC', 'SCS', 'EI', 'Auto-Entrepreneur']),
  activity_type: z.enum(['production', 'wholesale', 'retail', 'services', 'import_export', 'construction', 'artisan']),
  activity_code_nae: z.string().optional(),
  tax_regime: z.enum(['reel', 'ifu', 'forfaitaire']),
  estimated_annual_revenue: z.number().min(0),
  employee_count: z.number().min(0).int(),
  is_exporter: z.boolean(),
  is_importer: z.boolean(),
});

type ProfileFormData = z.infer<typeof profileSchema>;

interface CompanyProfileFormProps {
  initialData?: RCExtractedData | null;
  onComplete: (profile: CompanyLegalProfile) => void;
  onBack: () => void;
}

const LEGAL_FORMS: { value: LegalForm; label: string; description: string }[] = [
  { value: 'EURL', label: 'EURL', description: 'Entreprise Unipersonnelle à Responsabilité Limitée' },
  { value: 'SARL', label: 'SARL', description: 'Société à Responsabilité Limitée' },
  { value: 'SPA', label: 'SPA', description: 'Société Par Actions' },
  { value: 'SNC', label: 'SNC', description: 'Société en Nom Collectif' },
  { value: 'SCS', label: 'SCS', description: 'Société en Commandite Simple' },
  { value: 'EI', label: 'EI', description: 'Entreprise Individuelle' },
  { value: 'Auto-Entrepreneur', label: 'Auto-Entrepreneur', description: 'Auto-Entrepreneur' },
];

const ACTIVITY_TYPES: { value: ActivityType; label: string; description: string }[] = [
  { value: 'production', label: 'Production', description: 'Fabrication, industrie, manufacture' },
  { value: 'wholesale', label: 'Commerce en gros', description: 'Vente en gros, distribution' },
  { value: 'retail', label: 'Commerce de détail', description: 'Vente au détail, magasins' },
  { value: 'services', label: 'Services', description: 'Prestations de services, conseil' },
  { value: 'import_export', label: 'Import/Export', description: 'Commerce international' },
  { value: 'construction', label: 'Construction (BTP)', description: 'Bâtiment et travaux publics' },
  { value: 'artisan', label: 'Artisanat', description: 'Métiers artisanaux' },
];

const TAX_REGIMES: { value: TaxRegime; label: string; description: string }[] = [
  { value: 'reel', label: 'Régime Réel', description: 'Régime standard (CA > 15M DZD)' },
  { value: 'ifu', label: 'IFU', description: 'Impôt Forfaitaire Unique (CA < 15M DZD)' },
  { value: 'forfaitaire', label: 'Régime Forfaitaire', description: 'Régime simplifié' },
];

export function CompanyProfileForm({ initialData, onComplete, onBack }: CompanyProfileFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      legal_form: (initialData?.legal_form_fr as LegalForm) || 'SARL',
      activity_type: (initialData?.activity_fr as ActivityType) || 'wholesale',
      activity_code_nae: '',
      tax_regime: 'reel',
      estimated_annual_revenue: 0,
      employee_count: 0,
      is_exporter: false,
      is_importer: false,
    },
  });

  const watchedLegalForm = watch('legal_form');
  const watchedActivityType = watch('activity_type');
  const watchedTaxRegime = watch('tax_regime');
  const watchedIsExporter = watch('is_exporter');
  const watchedIsImporter = watch('is_importer');

  const onSubmit = async (data: ProfileFormData) => {
    setIsSubmitting(true);
    try {
      // Combine with initial data
      const profile: CompanyLegalProfile = {
        ...data,
        rc_extracted_data: initialData || undefined,
      };
      onComplete(profile);
    } catch (error) {
      console.error('Error submitting profile:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Legal Form */}
      <div>
        <Label htmlFor="legal_form">Forme juridique *</Label>
        <Select
          value={watchedLegalForm}
          onValueChange={(value) => setValue('legal_form', value as LegalForm)}
        >
          <SelectTrigger className="mt-1">
            <SelectValue placeholder="Sélectionnez la forme juridique" />
          </SelectTrigger>
          <SelectContent>
            {LEGAL_FORMS.map((form) => (
              <SelectItem key={form.value} value={form.value}>
                <div>
                  <div className="font-medium">{form.label}</div>
                  <div className="text-xs text-gray-500">{form.description}</div>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.legal_form && (
          <p className="mt-1 text-xs text-red-600">{errors.legal_form.message}</p>
        )}
      </div>

      {/* Activity Type */}
      <div>
        <Label htmlFor="activity_type">Type d'activité *</Label>
        <Select
          value={watchedActivityType}
          onValueChange={(value) => setValue('activity_type', value as ActivityType)}
        >
          <SelectTrigger className="mt-1">
            <SelectValue placeholder="Sélectionnez le type d'activité" />
          </SelectTrigger>
          <SelectContent>
            {ACTIVITY_TYPES.map((activity) => (
              <SelectItem key={activity.value} value={activity.value}>
                <div>
                  <div className="font-medium">{activity.label}</div>
                  <div className="text-xs text-gray-500">{activity.description}</div>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.activity_type && (
          <p className="mt-1 text-xs text-red-600">{errors.activity_type.message}</p>
        )}
      </div>

      {/* Activity Code NAE */}
      <div>
        <Label htmlFor="activity_code_nae">Code NAE (optionnel)</Label>
        <Input
          id="activity_code_nae"
          {...register('activity_code_nae')}
          placeholder="Ex: 47.11.10"
          className="mt-1"
        />
        <p className="mt-1 text-xs text-gray-500">
          Nomenclature des Activités Économiques
        </p>
      </div>

      {/* Tax Regime */}
      <div>
        <Label htmlFor="tax_regime">Régime fiscal *</Label>
        <Select
          value={watchedTaxRegime}
          onValueChange={(value) => setValue('tax_regime', value as TaxRegime)}
        >
          <SelectTrigger className="mt-1">
            <SelectValue placeholder="Sélectionnez le régime fiscal" />
          </SelectTrigger>
          <SelectContent>
            {TAX_REGIMES.map((regime) => (
              <SelectItem key={regime.value} value={regime.value}>
                <div>
                  <div className="font-medium">{regime.label}</div>
                  <div className="text-xs text-gray-500">{regime.description}</div>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.tax_regime && (
          <p className="mt-1 text-xs text-red-600">{errors.tax_regime.message}</p>
        )}
      </div>

      {/* Business Metrics */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="estimated_annual_revenue">Chiffre d'affaires annuel estimé (DZD) *</Label>
          <Input
            id="estimated_annual_revenue"
            type="number"
            {...register('estimated_annual_revenue', { valueAsNumber: true })}
            placeholder="0"
            step="1000"
            className="mt-1"
          />
          {errors.estimated_annual_revenue && (
            <p className="mt-1 text-xs text-red-600">{errors.estimated_annual_revenue.message}</p>
          )}
        </div>

        <div>
          <Label htmlFor="employee_count">Nombre d'employés *</Label>
          <Input
            id="employee_count"
            type="number"
            {...register('employee_count', { valueAsNumber: true })}
            placeholder="0"
            min="0"
            className="mt-1"
          />
          {errors.employee_count && (
            <p className="mt-1 text-xs text-red-600">{errors.employee_count.message}</p>
          )}
        </div>
      </div>

      {/* Special Status */}
      <div className="space-y-4 rounded-lg border p-4">
        <h3 className="font-medium">Statut spécial</h3>
        
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <Label htmlFor="is_exporter" className="cursor-pointer">
              Entreprise exportatrice
            </Label>
            <p className="text-xs text-gray-500">
              Votre entreprise exporte des biens ou services
            </p>
          </div>
          <Switch
            id="is_exporter"
            checked={watchedIsExporter}
            onCheckedChange={(checked) => setValue('is_exporter', checked)}
          />
        </div>

        <div className="flex items-center justify-between">
          <div className="flex-1">
            <Label htmlFor="is_importer" className="cursor-pointer">
              Entreprise importatrice
            </Label>
            <p className="text-xs text-gray-500">
              Votre entreprise importe des biens ou services
            </p>
          </div>
          <Switch
            id="is_importer"
            checked={watchedIsImporter}
            onCheckedChange={(checked) => setValue('is_importer', checked)}
          />
        </div>
      </div>

      {/* Info Box */}
      <div className="rounded-lg bg-blue-50 p-4 dark:bg-blue-950">
        <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100">
          💡 Pourquoi ces informations ?
        </h4>
        <p className="mt-1 text-xs text-blue-800 dark:text-blue-200">
          Ces informations nous permettent de déterminer automatiquement quelles obligations fiscales
          et légales s'appliquent à votre entreprise. Vous verrez un résumé à l'étape suivante.
        </p>
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-4">
        <Button type="button" variant="outline" onClick={onBack}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Retour
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? (
            <>Traitement...</>
          ) : (
            <>
              Continuer
              <ArrowRight className="ml-2 h-4 w-4" />
            </>
          )}
        </Button>
      </div>
    </form>
  );
}
